import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Center,
  Stack,
  Spinner
} from '@chakra-ui/react';
import { ScrapejobModel } from 'models/scrapejob/ScrapejobModel';
import { ArtistModel } from 'models';
import { ScrapesiteModel } from 'models/scrapejob/ScrapesiteModel';
import { ProcessStatus } from 'models/types';
import DetailedScrapeJobCard from '../DetailedScrapeJobCard/DetailedScrapeJobCard';
import ScrapeJobFiltersForm from '../../Form/ScrapeJobFiltersForm/ScrapeJobFiltersForm';
import EmptyState from '../../EmptyState/EmptyState';
import PaginationFooter from '../../Pagination/PaginationFooter/PaginationFooter';

export interface DetailedScrapeJobGridProps {
  scrapejobs: ScrapejobModel[];
  isLoading: boolean;
  onApprove?: (scrapejobId: string) => void;
  onReject?: (scrapejobId: string) => void;
  onSanitize?: (scrapejobId: string) => void;
  approveLoading?: boolean;
  rejectLoading?: boolean;
  sanitizeLoading?: boolean;
  defaultStatus?: ProcessStatus;
  onStatusChange?: (status: ProcessStatus) => void;
  availableArtists?: ArtistModel[];
  availableScrapesites?: ScrapesiteModel[];
  selectionMode?: boolean;
  selectedScrapejobs?: ScrapejobModel[];
  onScrapejobSelect?: (scrapejob: ScrapejobModel) => void;
  onFilteredScrapejobsChange?: (scrapejobs: ScrapejobModel[]) => void;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

export default function DetailedScrapeJobGrid({
  scrapejobs,
  isLoading,
  onApprove,
  onReject,
  onSanitize,
  approveLoading = false,
  rejectLoading = false,
  sanitizeLoading = false,
  defaultStatus = ProcessStatus.PENDING,
  onStatusChange,
  availableArtists = [],
  availableScrapesites = [],
  selectionMode = false,
  selectedScrapejobs = [],
  onScrapejobSelect,
  onFilteredScrapejobsChange,
  currentPage = 1,
  totalPages = 1,
  onPageChange
}: DetailedScrapeJobGridProps) {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(defaultStatus);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedArtists, setSelectedArtists] = useState<ArtistModel[]>([]);
  const [selectedScrapesites, setSelectedScrapesites] = useState<ScrapesiteModel[]>([]);

  // Filter scrapejobs by status, search query, and artists (client-side filtering only)
  const filteredScrapejobs = useMemo(() => {
    let filtered = scrapejobs;

    // Filter by status
    filtered = filtered.filter(scrapejob =>
      scrapejob.status.toLowerCase() === selectedStatus.toLowerCase()
    );

    // Filter by artists
    if (selectedArtists.length > 0) {
      const artistIds = new Set(selectedArtists.map(artist => artist.id));
      filtered = filtered.filter(scrapejob => {
        return Array.from(artistIds).some(artistId => scrapejob.containsArtist(artistId));
      });
    }

    if (selectedScrapesites.length > 0) {
      const scrapesiteIds = new Set(selectedScrapesites.map(site => site.id));
      filtered = filtered.filter(scrapejob => scrapesiteIds.has(scrapejob.siteId));
    }

    // Filter by search query (scrapesite name)
    if (searchQuery) {
      filtered = filtered.filter(scrapejob =>
        scrapejob.getSiteName().toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [scrapejobs, selectedStatus, searchQuery, selectedArtists, selectedScrapesites]);

  // Notify parent of filtered scrapejobs changes
  useEffect(() => {
    if (onFilteredScrapejobsChange) {
      onFilteredScrapejobsChange(filteredScrapejobs);
    }
  }, [filteredScrapejobs, onFilteredScrapejobsChange]);

  const handlePageChange = (newPage: number) => {
    onPageChange?.(newPage);
  };

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
    onPageChange?.(1); // Reset to first page when status changes
    onStatusChange?.(status);
  };

  const handleQueryChange = (query: string) => {
    setSearchQuery(query);
    onPageChange?.(1); // Reset to first page when search changes
  };

  const handleArtistChange = (artists: ArtistModel[]) => {
    setSelectedArtists(artists);
    onPageChange?.(1); // Reset to first page when artists change
  };

  const handleScrapesiteChange = (sites: ScrapesiteModel[]) => {
    setSelectedScrapesites(sites);
    onPageChange?.(1);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedArtists([]);
    setSelectedScrapesites([]);
    onPageChange?.(1);
  };

  const isOnFilterMode = (): boolean => {
    return searchQuery.length > 0 || selectedArtists.length > 0 || selectedScrapesites.length > 0;
  };

  const renderScrapeJobCards = () => {
    if (isLoading) {
      return (
        <Center p={8}>
          <Spinner size="xl" />
        </Center>
      );
    }

    if (!filteredScrapejobs?.length) {
      return (
        <EmptyState
          header="No Scrapejobs"
          description="No Results Found"
          showButton={isOnFilterMode()}
          onButtonClick={handleClearFilters}
          buttonText="Clear Filters"
        />
      );
    }

    return (
      <Stack spacing={4}>
        {filteredScrapejobs.map(scrapejob => {
          const isSelected = selectedScrapejobs.some(s => s.id === scrapejob.id);
          return (
            <DetailedScrapeJobCard
              key={`scrapejob_card_${scrapejob.id}`}
              scrapejob={scrapejob}
              onApprove={onApprove}
              onReject={onReject}
              onSanitize={onSanitize}
              approveLoading={approveLoading}
              rejectLoading={rejectLoading}
              sanitizeLoading={sanitizeLoading}
              selectionMode={selectionMode}
              selected={isSelected}
              onSelect={() => onScrapejobSelect?.(scrapejob)}
            />
          );
        })}
      </Stack>
    );
  };

  return (
    <Box width="100%">
      <Box mb={6}>
        <ScrapeJobFiltersForm
          defaultStatus={selectedStatus}
          defaultQuery={searchQuery}
          defaultArtists={selectedArtists}
          defaultScrapesites={selectedScrapesites}
          availableArtists={availableArtists}
          availableScrapesites={availableScrapesites}
          onStatusChange={handleStatusChange}
          onQueryChange={handleQueryChange}
          onArtistChange={handleArtistChange}
          onScrapesiteChange={handleScrapesiteChange}
        />
      </Box>
      {renderScrapeJobCards()}
      {totalPages > 1 && (
        <Box mt={8}>
          <PaginationFooter
            currentPage={currentPage}
            lastPage={totalPages}
            siblingsCount={1}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  );
}
